/**
 * Brand tokens, mirrored from the console app's `src/routes/layout.css`
 * (`@theme` block). Keep the two in sync when the brand palette changes.
 *
 * Two palettes of the same shape, one per theme variant. `BrandPalette` is
 * derived from the dark one, so a key added there is a type error in the light
 * one until it is filled in - the two cannot drift apart silently.
 *
 * Both are consumed twice over: `brand.css` declares them as `--bui-*` for
 * Backstage UI, and `devTheme.ts` builds a Material UI theme from each.
 */
export const brandDark = {
  // Zed-like neutral grays
  bg: '#111113',
  surface: '#1a1a1c',
  elevated: '#222224',
  hover: '#2a2a2c',

  // Borders
  border: '#2c2c2e',
  borderBright: '#3c3c3e',

  // Text
  text: '#d8d8d8',
  muted: '#8c8c8e',
  dim: '#565658',
  // The highest-contrast text the page can carry, for the rare surface that
  // needs to outrank `text`. Matches `textContrast` in @backstage/theme.
  contrast: '#ffffff',

  // Accent (muted blue, Zed-like)
  accent: '#5c8fcc',
  accentDim: '#5c8fcc1f',

  // Semantic (muted, not neon)
  success: '#4faa70',
  successDim: '#4faa701f',
  danger: '#c1666b',
  dangerDim: '#c1666b1f',
  merged: '#9068c2',
  mergedDim: '#9068c21f',
  warning: '#c98e42',
  warningDim: '#c98e421f',
} as const;

export type BrandPalette = { [K in keyof typeof brandDark]: string };

/**
 * The light counterpart. Not a mechanical inversion of the dark values:
 *
 * - The surface ladder flips direction. In the dark palette each step up is
 *   lighter (`bg` darkest, `hover` lightest); here the page is the lightest
 *   thing and each raised surface is a shade grayer, which is how a panel
 *   reads as raised on white.
 * - Every accent and status color is darkened until it clears 4.5:1 against
 *   `surface`. The dark palette's colors are tuned for a dark ground and land
 *   between 2.8:1 and 3.3:1 on white, which fails for text and for the small
 *   status dots the catalog uses.
 */
export const brandLight: BrandPalette = {
  // Surfaces, lightest first: the page, then cards, then what sits on a card.
  bg: '#fbfbfc',
  surface: '#ffffff',
  elevated: '#f2f2f4',
  hover: '#e9e9ed',

  // Borders
  border: '#e3e3e7',
  borderBright: '#cdcdd3',

  // Text
  text: '#1c1c1f',
  muted: '#63636a',
  dim: '#9a9aa2',
  contrast: '#000000',

  // Accent, darkened from #5c8fcc to 5.3:1 on white.
  accent: '#3a6ea8',
  accentDim: '#3a6ea814',

  // Semantic, each darkened to clear 4.5:1 on white.
  success: '#2f8552',
  successDim: '#2f855214',
  danger: '#b0454b',
  dangerDim: '#b0454b14',
  merged: '#6f4aa0',
  mergedDim: '#6f4aa014',
  warning: '#8f5f19',
  warningDim: '#8f5f1914',
};

export const fontSans = 'system-ui, -apple-system, "Segoe UI", sans-serif';
export const fontMono = '"JetBrains Mono", ui-monospace, monospace';
